import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MessageCircle, Phone, RefreshCw, Save, CheckCircle2 } from "lucide-react";
import AdminShell, { Tabs } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, getErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "requests", label: "RM Requests" },
  { id: "inquiries", label: "Inquiries" },
  { id: "trades", label: "Trades" },
  { id: "settings", label: "Settings" },
];

const REQUEST_STATUSES = ["pending", "contacted", "processing", "confirmed", "cancelled", "rejected"];

const INQUIRY_STATUSES = ["new", "contacted", "qualified", "won", "lost"];

function fmtINR(v) {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
}

export default function AdminOtc() {
  const { admin } = useAdminAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("requests");

  useEffect(() => {
    if (admin === false) nav("/admin/login");
  }, [admin, nav]);

  if (!admin) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  return (
    <AdminShell title="OTC desk" subtitle="RM requests, inquiries, trades and desk settings.">
      <OtcPauseBanner />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      <div>
        {tab === "requests" && <RequestsTab />}
        {tab === "inquiries" && <InquiriesTab />}
        {tab === "trades" && <TradesTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </AdminShell>
  );
}

function canConfirmRequest(row) {
  const fields = ["quoted_price_inr", "asset_qty", "fiat_total_inr", "fee_inr", "net_fiat_inr"];
  return fields.every((key) => {
    const val = row[key];
    return val != null && Number.isFinite(Number(val)) && Number(val) >= 0
      && (key === "fee_inr" || Number(val) > 0);
  });
}

function OtcPauseBanner() {
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/admin/safety/killswitch")
      .then(({ data }) => setPaused(!!data?.otc_paused))
      .catch(() => {});
  }, []);

  async function resumeDesk() {
    setBusy(true);
    try {
      await api.post("/api/admin/safety/killswitch", { key: "otc_paused", value: false });
      setPaused(false);
    } catch (_) {}
    finally {
      setBusy(false);
    }
  }

  if (!paused) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-[13px] text-amber-200">
      <span>
        OTC desk pause flag is ON in exchange settings. On older API builds this can block confirm.
        Click Resume or deploy the latest backend.
      </span>
      <button
        type="button"
        onClick={resumeDesk}
        disabled={busy}
        className="shrink-0 rounded-md bg-amber-500 px-3 py-1.5 text-[12px] font-semibold text-surface hover:bg-amber-400 disabled:opacity-60"
      >
        {busy ? "Resuming…" : "Resume OTC desk"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function RequestsTab() {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const pager = useAdminPager();

  function startEdit(row) {
    setEditingId(row.quote_id);
    setEditForm({
      quoted_price_inr: row.quoted_price_inr ?? "",
      asset_qty: row.asset_qty ?? "",
      fiat_total_inr: row.fiat_total_inr ?? "",
      fee_inr: row.fee_inr ?? "",
      net_fiat_inr: row.net_fiat_inr ?? "",
      rm_notes: row.rm_notes ?? "",
    });
  }

  async function saveQuote(row) {
    setErr("");
    const num = (v) => (v !== "" && v != null ? Number(v) : undefined);
    try {
      await api.patch(`/api/admin/otc/requests/${row.quote_id}`, {
        quoted_price_inr: num(editForm.quoted_price_inr),
        asset_qty: num(editForm.asset_qty),
        fiat_total_inr: num(editForm.fiat_total_inr),
        fee_inr: num(editForm.fee_inr),
        net_fiat_inr: num(editForm.net_fiat_inr),
        rm_notes: editForm.rm_notes || undefined,
      });
      setMsg(`Updated quote for ${row.quote_id}`);
      setEditingId(null);
      load();
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = {
        skip: pager.skip,
        limit: pager.limit,
        ...(statusFilter ? { status: statusFilter } : {}),
      };
      const { data } = await api.get("/api/admin/otc/requests", { params });
      const { items, total } = unwrapAdminList(data);
      setList(items);
      pager.setTotal(total);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    pager.resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, pager.page]);

  async function markContacted(row) {
    try {
      await api.post(`/api/admin/otc/requests/${row.quote_id}/contact`, {
        status: "contacted",
        rm_notes: row.rm_notes || "RM contacted user",
      });
      setMsg(`Marked ${row.quote_id} as contacted`);
      load();
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  async function confirmTrade(row) {
    setConfirmingId(row.quote_id);
    setErr("");
    try {
      await api.post(`/api/admin/otc/requests/${row.quote_id}/confirm`);
      setMsg(`Trade confirmed for ${row.user_email}`);
      load();
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setConfirmingId(null);
    }
  }

  async function rejectTrade(row) {
    try {
      await api.post(`/api/admin/otc/requests/${row.quote_id}/reject`, {
        status: "rejected",
        rm_notes: "Rejected by desk",
      });
      load();
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  function waLink(row) {
    const base = row.desk_whatsapp_url || "https://wa.me/";
    const phone = (row.user_mobile || "").replace(/\D/g, "");
    const text = encodeURIComponent(
      `Hi, this is the PaisaCX OTC desk regarding your ${row.side} ${row.asset} request (${row.quote_id}).`,
    );
    if (phone.length >= 10) {
      return `https://wa.me/91${phone.slice(-10)}?text=${text}`;
    }
    return `${base}?text=${text}`;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-otc-requests-filter"
          className="h-10 px-3 border border-border rounded-lg text-[13px] bg-surface"
        >
          <option value="">Open requests</option>
          {REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="h-10 px-3 border border-border rounded-lg text-[13px] font-semibold text-muted-foreground hover:text-white bg-surface flex items-center gap-1.5"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {err && <div className="text-red-400 text-[13px] mb-3">{err}</div>}
      {msg && <div className="text-emerald-400 text-[13px] mb-3">{msg}</div>}

      <div className="bg-surface rounded-xl border border-border overflow-x-auto">
        {loading && list.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-[13px]" data-testid="admin-otc-requests-empty">
            No OTC requests.
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-surface text-muted-foreground text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Trade</th>
                <th className="px-4 py-3 font-semibold text-right">Qty</th>
                <th className="px-4 py-3 font-semibold text-right">Total (₹)</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((row) => (
                <Fragment key={row.quote_id}>
                <tr data-testid={`admin-otc-request-${row.quote_id}`}>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {new Date(row.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-white">{row.user_name || row.user_email}</div>
                    <div className="text-[11px] text-muted-foreground">{row.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <a
                        href={`mailto:${row.user_email}?subject=PaisaCX OTC ${row.quote_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-line text-[11px] text-white hover:bg-line-strong"
                        title="Email user"
                      >
                        <Mail size={12} /> Email
                      </a>
                      {row.user_mobile && (
                        <a
                          href={`tel:${row.user_mobile}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-line text-[11px] text-white hover:bg-line-strong"
                          title="Call user"
                        >
                          <Phone size={12} /> Call
                        </a>
                      )}
                      <a
                        href={waLink(row)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-line text-[11px] text-emerald-400 hover:bg-line-strong"
                        title="WhatsApp user"
                      >
                        <MessageCircle size={12} /> WhatsApp
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded ${row.side === "buy" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                      {row.side}
                    </span>
                    <span className="ml-2 font-semibold text-white">{row.asset}</span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-white">{row.asset_qty}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-white">{fmtINR(row.fiat_total_inr)}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded bg-amber-50 text-amber-600">
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {row.status === "pending" && (
                        <button
                          type="button"
                          onClick={() => markContacted(row)}
                          className="px-2.5 py-1.5 rounded bg-line text-[11px] font-semibold text-white hover:bg-line-strong"
                        >
                          Mark contacted
                        </button>
                      )}
                      {(row.status === "pending" || row.status === "contacted") && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className="px-2.5 py-1.5 rounded bg-line text-[11px] font-semibold text-amber-300 hover:bg-line-strong"
                          >
                            Edit quote
                          </button>
                          <button
                            type="button"
                            onClick={() => confirmTrade(row)}
                            disabled={confirmingId === row.quote_id || !canConfirmRequest(row)}
                            title={!canConfirmRequest(row) ? "Edit and save the final quote before confirming" : undefined}
                            data-testid={`admin-otc-confirm-${row.quote_id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-emerald-600 text-[11px] font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                          >
                            <CheckCircle2 size={12} />
                            {confirmingId === row.quote_id ? "Confirming…" : "Confirm trade"}
                          </button>
                          <button
                            type="button"
                            onClick={() => rejectTrade(row)}
                            className="px-2.5 py-1.5 rounded bg-red-900/40 text-[11px] font-semibold text-red-300 hover:bg-red-900/60"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {editingId === row.quote_id && (
                  <tr className="bg-background">
                    <td colSpan={8} className="px-4 py-4">
                      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
                        {[
                          ["quoted_price_inr", "Price (₹)"],
                          ["asset_qty", "Qty"],
                          ["fiat_total_inr", "Total (₹)"],
                          ["fee_inr", "Fee (₹)"],
                          ["net_fiat_inr", "Net (₹)"],
                        ].map(([key, label]) => (
                          <label key={key} className="block">
                            <span className="text-[10px] uppercase text-muted-foreground">{label}</span>
                            <input
                              type="number"
                              step="any"
                              value={editForm[key]}
                              onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                              className="mt-1 w-full h-9 px-2 rounded bg-surface border border-border text-white text-[12px]"
                            />
                          </label>
                        ))}
                        <label className="block sm:col-span-3 lg:col-span-1">
                          <span className="text-[10px] uppercase text-muted-foreground">RM notes</span>
                          <input
                            type="text"
                            value={editForm.rm_notes}
                            onChange={(e) => setEditForm({ ...editForm, rm_notes: e.target.value })}
                            className="mt-1 w-full h-9 px-2 rounded bg-surface border border-border text-white text-[12px]"
                          />
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => saveQuote(row)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-[11px] font-semibold text-white"
                        >
                          <Save size={12} /> Save quote
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="px-3 py-1.5 rounded bg-line text-[11px] font-semibold text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          loading={loading}
          onPage={pager.setPage}
          testId="otc-requests-pager"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function InquiriesTab() {
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const pager = useAdminPager();

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = {
        skip: pager.skip,
        limit: pager.limit,
        ...(statusFilter ? { status: statusFilter } : {}),
      };
      const { data } = await api.get("/api/admin/otc/inquiries", { params });
      const { items, total } = unwrapAdminList(data);
      setList(items);
      pager.setTotal(total);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, pager.page]);

  async function updateStatus(id, status) {
    try {
      await api.patch(`/api/admin/otc/inquiries/${id}`, { status });
      load();
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-otc-status-filter"
          className="h-10 px-3 border border-border rounded-lg text-[13px] bg-surface"
        >
          <option value="">All statuses</option>
          {INQUIRY_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          onClick={load}
          className="h-10 px-3 border border-border rounded-lg text-[13px] font-semibold text-muted-foreground hover:text-white bg-surface flex items-center gap-1.5"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {err && <div className="text-red-600 text-[13px] mb-3">{err}</div>}

      <div className="bg-surface rounded-xl border border-border overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-[13px]">No inquiries.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-surface text-muted-foreground text-left">
              <tr>
                <th className="px-5 py-3 font-semibold">When</th>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Contact</th>
                <th className="px-5 py-3 font-semibold">Side</th>
                <th className="px-5 py-3 font-semibold">Asset</th>
                <th className="px-5 py-3 font-semibold text-right">Size</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((row) => (
                <tr key={row.id} data-testid={`admin-otc-inquiry-${row.id}`}>
                  <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">
                    {new Date(row.created_at).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </td>
                  <td className="px-5 py-3 font-semibold text-white">{row.name}</td>
                  <td className="px-5 py-3 text-muted-foreground">
                    <div>{row.email}</div>
                    <div className="text-[11px]">{row.mobile}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-[10px] font-semibold uppercase px-2 py-1 rounded ${
                        row.side === "buy" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                      }`}
                    >
                      {row.side}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-semibold">{row.asset}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold text-white">
                    {fmtINR(row.estimated_size_inr)}
                  </td>
                  <td className="px-5 py-3">
                    <select
                      value={row.status}
                      onChange={(e) => updateStatus(row.id, e.target.value)}
                      data-testid={`admin-otc-status-${row.id}`}
                      className="h-8 px-2 border border-border rounded-md text-[12px] bg-surface"
                    >
                      {INQUIRY_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground text-[12px] max-w-[260px]">
                    {row.notes || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          loading={loading}
          onPage={pager.setPage}
          testId="otc-inquiries-pager"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TradesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const pager = useAdminPager();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/otc/trades", {
        params: { skip: pager.skip, limit: pager.limit },
      });
      const { items, total } = unwrapAdminList(data);
      setList(items);
      pager.setTotal(total);
    } catch (_) {}
    setLoading(false);
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pager.page]);

  return (
    <div className="bg-surface rounded-xl border border-border overflow-x-auto">
      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground text-[13px]" data-testid="admin-otc-trades-empty">
          No OTC trades yet.
        </div>
      ) : (
        <table className="w-full text-[13px]">
          <thead className="bg-surface text-muted-foreground text-left">
            <tr>
              <th className="px-5 py-3 font-semibold">When</th>
              <th className="px-5 py-3 font-semibold">User</th>
              <th className="px-5 py-3 font-semibold">Side</th>
              <th className="px-5 py-3 font-semibold">Asset</th>
              <th className="px-5 py-3 font-semibold text-right">Qty</th>
              <th className="px-5 py-3 font-semibold text-right">Price (₹)</th>
              <th className="px-5 py-3 font-semibold text-right">Total (₹)</th>
              <th className="px-5 py-3 font-semibold text-right">Fee (₹)</th>
              <th className="px-5 py-3 font-semibold">Settled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {list.map((t) => (
              <tr key={t.quote_id}>
                <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">
                  {new Date(t.created_at).toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </td>
                <td className="px-5 py-3 text-white">{t.user_email}</td>
                <td className="px-5 py-3">
                  <span
                    className={`text-[10px] font-semibold uppercase px-2 py-1 rounded ${
                      t.side === "buy" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                    }`}
                  >
                    {t.side}
                  </span>
                </td>
                <td className="px-5 py-3 font-semibold">{t.asset}</td>
                <td className="px-5 py-3 text-right tabular-nums">{t.asset_qty}</td>
                <td className="px-5 py-3 text-right tabular-nums">{fmtINR(t.quoted_price_inr)}</td>
                <td className="px-5 py-3 text-right tabular-nums font-semibold">{fmtINR(t.fiat_total_inr)}</td>
                <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">{fmtINR(t.fee_inr)}</td>
                <td className="px-5 py-3">
                  <span
                    className={`text-[10px] font-semibold uppercase px-2 py-1 rounded ${
                      t.settled ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                    }`}
                  >
                    {t.settled ? "Settled" : "Pending"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <AdminPager
        page={pager.page}
        total={pager.total}
        pageSize={pager.pageSize}
        loading={loading}
        onPage={pager.setPage}
        testId="otc-trades-pager"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/api/admin/otc/settings")
      .then(({ data }) => setS(data))
      .catch((e) => setErr(getErrorMessage(e)));
  }, []);

  async function save() {
    setSaving(true);
    setErr("");
    setMsg("");
    try {
      const { data } = await api.put("/api/admin/otc/settings", {
        spread_buy_pct: Number(s.spread_buy_pct),
        spread_sell_pct: Number(s.spread_sell_pct),
        fee_pct: Number(s.fee_pct),
        min_trade_inr: Number(s.min_trade_inr),
        max_trade_inr: Number(s.max_trade_inr),
        quote_validity_seconds: Number(s.quote_validity_seconds),
        contact_email: s.contact_email,
        contact_phone: s.contact_phone,
        whatsapp_url: s.whatsapp_url,
      });
      setS(data);
      setMsg("Saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  if (!s) return <div className="text-muted-foreground text-[13px]">Loading settings…</div>;

  return (
    <div className="bg-surface rounded-xl border border-border p-7 max-w-[640px]">
      <h3 className="text-[16px] font-semibold text-white mb-5">Pricing & limits</h3>
      <div className="grid grid-cols-2 gap-4">
        <NumField label="Spread Buy (%)" value={s.spread_buy_pct} onChange={(v) => setS({ ...s, spread_buy_pct: v })} step="0.05" />
        <NumField label="Spread Sell (%)" value={s.spread_sell_pct} onChange={(v) => setS({ ...s, spread_sell_pct: v })} step="0.05" />
        <NumField label="Fee (%)" value={s.fee_pct} onChange={(v) => setS({ ...s, fee_pct: v })} step="0.05" />
        <NumField label="Quote validity (s)" value={s.quote_validity_seconds} onChange={(v) => setS({ ...s, quote_validity_seconds: v })} step="1" />
        <NumField label="Min trade (₹)" value={s.min_trade_inr} onChange={(v) => setS({ ...s, min_trade_inr: v })} step="1000" />
        <NumField label="Max trade (₹)" value={s.max_trade_inr} onChange={(v) => setS({ ...s, max_trade_inr: v })} step="1000" />
      </div>

      <h3 className="text-[16px] font-semibold text-white mt-7 mb-3">Desk contact</h3>
      <div className="space-y-3">
        <TextField label="Email" value={s.contact_email || ""} onChange={(v) => setS({ ...s, contact_email: v })} />
        <TextField label="Phone" value={s.contact_phone || ""} onChange={(v) => setS({ ...s, contact_phone: v })} />
        <TextField label="WhatsApp URL" value={s.whatsapp_url || ""} onChange={(v) => setS({ ...s, whatsapp_url: v })} />
      </div>

      {err && <div className="mt-4 text-[13px] text-red-600">{err}</div>}
      {msg && <div className="mt-4 text-[13px] text-emerald-600">{msg}</div>}

      <button
        onClick={save}
        disabled={saving}
        data-testid="admin-otc-save"
        className="mt-6 inline-flex items-center gap-2 h-[44px] px-6 rounded-lg bg-primary text-white font-semibold hover:bg-primary-deep transition disabled:opacity-60"
      >
        <Save size={14} /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

function NumField({ label, value, onChange, step }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold text-white mb-1.5">{label}</span>
      <input
        type="number"
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-[42px] px-3 border rounded-lg text-[14px] outline-none tabular-nums bg-background text-white border-line placeholder:text-dim focus:border-primary transition"
      />
    </label>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-semibold text-white mb-1.5">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-[42px] px-3 border rounded-lg text-[14px] outline-none bg-background text-white border-line placeholder:text-dim focus:border-primary transition"
      />
    </label>
  );
}
